/**
 * CP3Generating — portrait generation in progress.
 * data-screen-label="cp-03 Blending portraits"
 */

import { useTranslation } from 'react-i18next';
import { CPBackdrop } from './shared';
import { LivingSigil } from '../../brand/LivingSigil';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import { coupleStepStatuses } from './coupleSteps';
import styles from './Couple.module.css';

interface CP3GeneratingProps {
  flow: UseCoupleFlowReturn;
}

const STEP_KEYS = [
  'couple.generating.step1',
  'couple.generating.step2',
  'couple.generating.step3',
] as const;

export function CP3Generating({ flow }: CP3GeneratingProps): JSX.Element {
  const { t } = useTranslation('flows');
  // Chip statuses follow the REAL flow state, not a wall-clock timer, so a step
  // is never shown "done" before the portrait actually completes.
  const statuses = coupleStepStatuses(flow.state, STEP_KEYS.length);

  return (
    <div className={styles.root} data-screen-label="cp-03 Blending portraits">
      <CPBackdrop tone="rose" />

      <div className={styles.content}>
        <div className={styles.generatingCenter}>
          <LivingSigil archetype="celestial-romantic" state="breathing" size={220} />

          <p className={styles.generatingSubtitle}>{t('couple.generating.subtitle')}</p>

          <div className={styles.progressSteps}>
            {STEP_KEYS.map((key, i) => {
              const status = statuses[i] ?? 'pending';
              const done = status === 'done';
              const active = status === 'active';

              return (
                <div key={key} className={styles.progressStep}>
                  <span className={done ? styles.progressStepCheck : styles.progressStepDots}>
                    {done ? '✓' : active ? <span className={styles.dotsBlink}>···</span> : '○'}
                  </span>
                  <span style={{ color: done ? 'var(--cream)' : 'var(--fg-2)' }}>
                    {t(key)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
